import React, { useEffect, useState } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const TYPE_COLORS = {
  basics: "#2a9d8f",
  listening: "#e76f51",
  "listening to english": "#e76f51",
  tracing: "#e9c46a",
  story: "#f4a261",
  "story review": "#f4a261",
  writing: "#ff7f9a",
  match: "#9b59b6",
  "match to english": "#9b59b6",
  speaking: "#50C878",
  test: "#3a82c6",
};

function typeColor(type) {
  return TYPE_COLORS[(type || "").toLowerCase()] || "#cccccc";
}

// ── Stat Card ────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, shadow }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 20,
        border: "1.5px solid #E0E0E0",
        borderBottom: `4px solid ${shadow}`,
        padding: "24px 28px",
        flex: "1 1 160px",
        minWidth: 150,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ fontSize: 30 }}>{icon}</div>
      <div
        style={{
          fontSize: "2.1rem",
          fontWeight: 700,
          color,
          fontFamily: "'Quicksand', sans-serif",
          lineHeight: 1,
        }}
      >
        {value.toLocaleString()}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "#606060",
          fontFamily: "'Quicksand', sans-serif",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ── Section Card wrapper ─────────────────────────────────────────
function SectionCard({ title, action, onAction, children }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 20,
        border: "1px solid #E0E0E0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        flex: "1 1 300px",
        minWidth: 260,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 20px",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 15,
            fontFamily: "'Quicksand', sans-serif",
            color: "#000",
          }}
        >
          {title}
        </span>
        {action && (
          <button
            onClick={onAction}
            style={{
              background: "none",
              border: "none",
              color: "#2a9d8f",
              fontFamily: "'Quicksand', sans-serif",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              padding: 0,
            }}
          >
            {action}
          </button>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ── User Row ─────────────────────────────────────────────────────
function UserRow({ name, email, isLast }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 20px",
        borderBottom: isLast ? "none" : "1px solid #f3f3f3",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "#2a9d8f",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
          fontFamily: "'Quicksand', sans-serif",
        }}
      >
        {initials}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#606060",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {email}
        </div>
      </div>
    </div>
  );
}

// ── Lesson Row ───────────────────────────────────────────────────
function LessonRow({ title, concept, type, isLast }) {
  const color = typeColor(type);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 20px",
        borderBottom: isLast ? "none" : "1px solid #f3f3f3",
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#606060",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {concept}
        </div>
      </div>
      {type && (
        <span
          style={{
            background: color,
            color: "#fff",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            padding: "3px 10px",
            textTransform: "capitalize",
            flexShrink: 0,
            fontFamily: "'Quicksand', sans-serif",
            whiteSpace: "nowrap",
          }}
        >
          {type}
        </span>
      )}
    </div>
  );
}

// ── Bar Row ──────────────────────────────────────────────────────
function BarRow({ label, value, max, isLast, barColor = "#2a9d8f" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(90px, 180px) 1fr 44px",
        alignItems: "center",
        gap: 14,
        padding: "10px 20px",
        borderBottom: isLast ? "none" : "1px solid #f3f3f3",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: "#000",
        }}
      >
        {label}
      </div>
      <div
        style={{
          background: "#f0f0f0",
          borderRadius: 999,
          height: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: barColor,
            borderRadius: 999,
          }}
        />
      </div>
      <div
        style={{
          fontWeight: 700,
          fontSize: 13,
          color: barColor,
          textAlign: "right",
          fontFamily: "'Quicksand', sans-serif",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ── Empty State ──────────────────────────────────────────────────
function Empty({ message }) {
  return (
    <div
      style={{
        padding: "20px",
        color: "#606060",
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
        fontFamily: "'Quicksand', sans-serif",
      }}
    >
      {message}
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────
function Dashboard() {
  const [userCount, setUserCount] = useState(0);
  const [lessonCount, setLessonCount] = useState(0);
  const [conceptCount, setConceptCount] = useState(0);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentLessons, setRecentLessons] = useState([]);
  const [activeConcepts, setActiveConcepts] = useState([]);
  const [lessonsByType, setLessonsByType] = useState([]);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async () => {
      const [usersSnap, lessonsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "lessons")),
      ]);

      // ── Users ──────────────────────────────────────────────────
      setUserCount(usersSnap.size);

      const recentArr = [];

      usersSnap.forEach((doc) => {
        const d = doc.data();
        recentArr.push({
          id: doc.id,
          name: d.displayName || d.name || "Unknown",
          email: d.email || "",
          createdAt: d.createdAt || null,
        });
      });

      recentArr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setRecentUsers(recentArr.slice(0, 5));

      // ── Lessons ────────────────────────────────────────────────
      setLessonCount(lessonsSnap.size);

      const concepts = new Set();
      const conceptActivity = {};
      const typeActivity = {};
      const lessonInfo = {};
      const recentLessonsArr = [];

      lessonsSnap.forEach((doc) => {
        const d = doc.data();
        lessonInfo[doc.id] = { title: d.title || doc.id, concept: d.concept || "Unknown" };
        if (d.concept) {
          concepts.add(d.concept);
          conceptActivity[d.concept] = (conceptActivity[d.concept] || 0) + 1;
        }
        if (d.type) {
          typeActivity[d.type] = (typeActivity[d.type] || 0) + 1;
        }
        recentLessonsArr.push({
          id: doc.id,
          title: d.title || doc.id,
          concept: d.concept || "Unknown",
          type: d.type || "",
          updated: d.updated || null,
          createdAt: d.createdAt || null,
        });
      });

      setConceptCount(concepts.size);

      recentLessonsArr.sort(
        (a, b) => (b.updated || b.createdAt || 0) - (a.updated || a.createdAt || 0)
      );
      setRecentLessons(recentLessonsArr.slice(0, 5));

      setActiveConcepts(
        Object.entries(conceptActivity)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([concept, count]) => ({ concept, count }))
      );

      setLessonsByType(
        Object.entries(typeActivity)
          .sort((a, b) => b[1] - a[1])
          .map(([type, count]) => ({ type, count }))
      );

      setLoading(false);
    };

    fetchAll();
  }, [db]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const maxByType = lessonsByType[0]?.count || 1;
  const maxConcept = activeConcepts[0]?.count || 1;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          color: "#606060",
          fontFamily: "'Quicksand', sans-serif",
          fontWeight: 600,
          fontSize: 15,
          gap: 12,
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            border: "2.5px solid #E0E0E0",
            borderTop: "2.5px solid #2a9d8f",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        Loading dashboard…
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1200 }}>

      {/* ── Page Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 28,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Dashboard</h1>
          <div style={{ color: "#606060", fontWeight: 600, fontSize: 14, marginTop: 4 }}>
            {today}
          </div>
        </div>
        <button
          className="primary-button"
          onClick={() => navigate("/lessons")}
          style={{
            backgroundColor: "#2a9d8f",
            color: "#fff",
            borderColor: "#258D80",
            fontSize: 14,
            padding: "9px 20px",
          }}
        >
          + Add Lesson
        </button>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <StatCard icon="👥" label="Total Users"   value={userCount}   color="#2a9d8f" shadow="#258D80" />
        <StatCard icon="📚" label="Total Lessons" value={lessonCount} color="#e76f51" shadow="#cf6348" />
        <StatCard icon="🧠" label="Concepts"      value={conceptCount} color="#3a82c6" shadow="#317aa8" />
      </div>

      {/* ── Recent Users + Recent Lessons ── */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 20 }}>
        <SectionCard
          title="Recent Users"
          action="View all →"
          onAction={() => navigate("/users")}
        >
          {recentUsers.length === 0 ? (
            <Empty message="No users yet." />
          ) : (
            recentUsers.map((u, i) => (
              <UserRow
                key={u.id}
                name={u.name}
                email={u.email}
                isLast={i === recentUsers.length - 1}
              />
            ))
          )}
        </SectionCard>

        <SectionCard
          title="Recent Lessons"
          action="View all →"
          onAction={() => navigate("/lessons")}
        >
          {recentLessons.length === 0 ? (
            <Empty message="No lessons yet." />
          ) : (
            recentLessons.map((l, i) => (
              <LessonRow
                key={l.id}
                title={l.title}
                concept={l.concept}
                type={l.type}
                isLast={i === recentLessons.length - 1}
              />
            ))
          )}
        </SectionCard>
      </div>

      {/* ── Lessons by Type + Most Active Concepts ── */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <SectionCard title="Lessons by Type">
          {lessonsByType.length === 0 ? (
            <Empty message="No lessons yet." />
          ) : (
            lessonsByType.map((t, i) => (
              <BarRow
                key={t.type}
                label={t.type}
                value={t.count}
                max={maxByType}
                barColor={typeColor(t.type)}
                isLast={i === lessonsByType.length - 1}
              />
            ))
          )}
        </SectionCard>

        <SectionCard title="Most Active Concepts">
          {activeConcepts.length === 0 ? (
            <Empty message="No concept activity yet." />
          ) : (
            activeConcepts.map((c, i) => (
              <BarRow
                key={c.concept}
                label={c.concept}
                value={c.count}
                max={maxConcept}
                isLast={i === activeConcepts.length - 1}
              />
            ))
          )}
        </SectionCard>
      </div>

    </div>
  );
}

export default Dashboard;
